/* eslint-disable no-restricted-syntax */
import { useAudio } from '@utils/providers/audio-context.tsx';
import React, {
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useMemo
} from 'react';
import { Descendant } from 'slate';
import { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import { currentWordIsSeeking } from '@utils/editors/current-work-is-seeking.ts';
import { useSlate } from 'slate-react';
import { getSelectedWordFromSelection } from '@utils/editors/get-selected-word-from-selection.ts';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';

type Props = RenderLeafProps & {
  leaf: Descendant & DocumentTranscriptTextV1;
};

export const SharedParagraphLeaf = React.memo(
  ({ attributes, children, leaf }: PropsWithChildren & Props) => {
    const {
      setStartStopWord,
      clickedComment,
      setPopoverPosition,
      setPopoverOpen
    } = useSharedTranscript();
    const { currentTime, setTime } = useAudio();
    const isCurrentWord = currentWordIsSeeking(leaf, currentTime);
    const editor = useSlate();

    const isInStartStopWordRange =
      (leaf.start ?? 0) <= (Number(clickedComment?.index_to) ?? 0) &&
      (leaf.stop ?? 0) >= (Number(clickedComment?.index_from) ?? 0);

    const handleSelect = (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      const selectedWordElement = getSelectedWordFromSelection(editor);
      if (selectedWordElement) {
        setStartStopWord(selectedWordElement);
        setTime(selectedWordElement.start / 1000);

        const rect = e.currentTarget?.getBoundingClientRect();
        if (rect) {
          setPopoverPosition({
            x: rect.left + window.scrollX,
            y: rect.top + window.scrollY - 65
          });
          setPopoverOpen(true);
        }
      }
    };

    const spanStyle = useMemo(() => {
      // Apply 50ms shift for visual highlighting consistency
      const shiftedTime = currentTime * 1000 + 50;

      return {
        color: (() => {
          if (isCurrentWord) return '#0a8df8';
          if (leaf.stop && leaf.stop <= shiftedTime) return '#000000'; // Passed words
          if (leaf.start && leaf.start > shiftedTime) return '#707070'; // Future words
          return 'unset';
        })(),
        fontStyle: leaf.italic ? 'italic' : 'normal',
        backgroundColor: leaf.highlight
          ? '#ffeeba'
          : isInStartStopWordRange
            ? '#dbdde3'
            : leaf.mark
              ? leaf.color || '#faf600'
              : '',
        border:
          leaf.highlight && leaf.isCurrentMatch ? '1px solid #afafaf' : 'unset',
        borderRadius: leaf.highlight && leaf.isCurrentMatch ? '3px' : 'unset',
        textDecoration: `${leaf.underlined ? 'underline ' : ''}${leaf.strike ? 'line-through' : ''}`,
        fontWeight: leaf.bold ? 'bold' : 'normal',
        fontSize: '18px'
      };
    }, [isCurrentWord, leaf, currentTime, isInStartStopWordRange]);

    const handleMouseUp = useCallback(
      (e: MouseEvent<HTMLSpanElement>) => handleSelect(e),
      [handleSelect]
    );

    return (
      <span
        data-start={leaf.start}
        data-stop={leaf.stop}
        onMouseUp={handleMouseUp}
        style={spanStyle}
        {...attributes}
        {...(leaf.highlight && { 'data-cy': 'search-highlighted' })}
      >
        {children}
      </span>
    );
  }
);
